import { invalidLatLng } from "@/lib/crg_apis/types/latlng";
import { maxLssPoints, maxLssPolygonArea } from "@/tools/create/lss-from-map/constants";
import { CreateLssFromMapSidebar } from "@/tools/create/lss-from-map/sidebar";
import { CreateLssFromMapStage, PointWithMetadata } from "@/tools/create/lss-from-map/types";
import { LargeSiteScreenTutorial } from "@/tools/risk-bands/large-site-screen/components/LargeSiteScreenTutorial";
import { Tool, ToolProps } from "@/types/tools";
import { GatewayOutlined } from "@ant-design/icons";
import * as turf from "@turf/turf";
import { Col, Row, Steps } from "antd";
import classnames from "classnames";
import { Polygon } from "leaflet";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
const PolygonPlotter = dynamic(() => import("./map"), {
  ssr: false
});
const steps: Record<CreateLssFromMapStage, string> = {
  polygon: "Define Large Site",
  points: "Edit Grid Points",
  settings: "Confirm Settings"
};
const LssFromMapSubTool = ({
  ...props
}) => {
  const [focusPoint, setFocusPoint] = useState(invalidLatLng);
  const [points, setPoints] = useState<PointWithMetadata[]>([]);
  const [polygons, setPolygons] = useState<Polygon[]>([]);
  const [stage, setStage] = useState<CreateLssFromMapStage>("polygon");
  const [totalArea, setTotalArea] = useState(0);

  /** Whether or not the polygons are within acceptable area limits. */
  const isAreaValid = totalArea > 0 && totalArea <= maxLssPolygonArea;
  /** Whether or not the number of points is valid, as well as positions. */
  const arePointsValid = points.length > 0 && points.length <= maxLssPoints && points.every(({
    isInPolygon
  }) => isInPolygon);
  useEffect(() => {
    // Calculate and set the total area of all polygons.
    const newTotalArea = polygons.reduce<number>((total, polygon) => {
      const geoJson = polygon.toGeoJSON();
      return turf.getType(geoJson) === "Polygon" ? total + turf.area(geoJson) : total;
    }, 0);
    setTotalArea(newTotalArea);
  }, [polygons]);
  return <Row className="w-full" gutter={[0, 16]} data-sentry-element="Row" data-sentry-component="LssFromMapSubTool" data-sentry-source-file="index.tsx">
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="index.tsx">
        <div className="flex items-center justify-between">
          <Steps className="col-2 mx-auto max-w-[600px] justify-self-center [&_.ant-collapse-header]:!mt-0" current={Object.keys(steps).indexOf(stage)} items={[...Object.values(steps).map(title => ({
          title
        })), {
          title: "Review"
        }]} labelPlacement="horizontal" size="small" data-sentry-element="Steps" data-sentry-source-file="index.tsx" />
        </div>
      </Col>
      <Row gutter={[16, 0]} className={classnames(styles.container, "mb-8 w-full")} data-sentry-element="Row" data-sentry-source-file="index.tsx">
        <Col flex={1} data-sentry-element="Col" data-sentry-source-file="index.tsx">
          <PolygonPlotter className="h-[70vh] w-full overflow-hidden rounded-md border" focusPoint={focusPoint} isAreaValid={isAreaValid} points={points} polygons={polygons} stage={stage} setPoints={setPoints} setPolygons={setPolygons} totalArea={totalArea} data-sentry-element="PolygonPlotter" data-sentry-source-file="index.tsx" />
        </Col>
        <Col flex={0} className="w-full max-w-[300px]" data-sentry-element="Col" data-sentry-source-file="index.tsx">
          <CreateLssFromMapSidebar arePointsValid={arePointsValid} isAreaValid={isAreaValid} points={points} polygons={polygons} setFocusPoint={setFocusPoint} setPolygons={setPolygons} setStage={setStage} stage={stage} data-sentry-element="CreateLssFromMapSidebar" data-sentry-source-file="index.tsx" />
        </Col>
      </Row>
      <LargeSiteScreenTutorial onClose={() => props.setIsTutorialOpen(false)} open={props.isTutorialOpen} setState={
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {}} data-sentry-element="LargeSiteScreenTutorial" data-sentry-source-file="index.tsx" />
    </Row>;
};
const createLssFromMapSubTool = {
  id: "lss-from-map",
  category: "create",
  keyPrefix: "create.lssFromMap",
  icon: <GatewayOutlined />,
  hasTutorial: true,
  render: (props: ToolProps) => <LssFromMapSubTool {...props} />,
  maxWidth: "1400px"
} as Tool;
export default createLssFromMapSubTool;